/**
 * This file contains the complete filter objects for the frontend Algolia filters (including their widgets).
 * If you update it, you need to also update frontend-filter-options.ts,
 * which contains the basic filter definitions.
 * These need to be in separate files because the ContentfulController seems to have issues
 * importing the instantsearch.js widgets.
 */

import { filterOptions, getFilterSetIdentifier } from './frontend-filter-options';
import { FilterData, WidgetType } from './algolia-types';

import {
  panel,
  rangeSlider,
  refinementList,
  toggleRefinement,
} from 'instantsearch.js/es/widgets';
import { parseFirstNumber } from '../../../resources/js/modules/utils';

export class Filter {
  public title: string;
  public attribute: string;
  public id: string;
  public limit?: number;
  public showMore?: boolean;
  public widgetType: WidgetType;
  /** This is a function that *returns* the widget,
   * since instantiating the widget requires the DOM element to be on the page,
   * and some are omitted on specific collections */
  public generateWidget: () => any; // TODO: Add types

  constructor(filterData: FilterData, generateWidget: () => any) {
    this.title = filterData.title;
    this.attribute = filterData.attribute;
    this.id = filterData.id;
    this.limit = filterData.limit;
    this.showMore = filterData.showMore;
    this.widgetType = filterData.widgetType;
    this.generateWidget = generateWidget;
    // this.widget = widget.bind(this);
  }

  // createWidget() {
  //   return this.widget;
  // }
}

/**
 * Wrap an algolia widget with a panel widget
 * @param filter The filter data
 * @param widget The widget to wrap with a panel
 * @param widgetOptions The options to pass to the widget
 */
const wrapWithPanel = (filter: FilterData, widget, widgetOptions) => {
  const p = panel({
    hidden(options) {
      const { items } = options;
      // Range slider doesn't have `options`, so always show it
      const hasItems = items && Array.isArray(items);
      if (!hasItems) {
        return false;
      }
      // Hide if only single option (or none)
      return items.length <= 1;
    },
    templates: {
      // @ts-ignore
      header(options, { html }) {
        return html`<button
          class="filters__accordion-toggle js-accordion-toggle"
        >
          <h4 class="filters__filter-title">${filter.title}</h4>
        </button>`;
      },
    },
  })(widget);

  return p(widgetOptions);
};

/**
 * Definition of all the different filters we could use
 * NOTE: You need to add to the filterOptions object in frontend-filter-options.ts
 * when adding a new filter here
 */
const filterObjects: Record<string, Filter> = {
  availableOnline: new Filter(filterOptions['availableOnline'], () => toggleRefinement({
    container: `#${filterOptions['availableOnline'].id}`,
    attribute: filterOptions['availableOnline'].attribute,
    on: 'online',
    templates: {
      labelText(_item, { html }) {
        return html`Available Online`;
      },
    },
  })),
  // This is labelled as "Product Type" in the UI
  category: new Filter(filterOptions['category'],
    () => wrapWithPanel(filterOptions['category'], refinementList, {
      container: `#${filterOptions['category'].id}`,
      attribute: filterOptions['category'].attribute,
      limit: 50,
    })),
  collection: new Filter(
    filterOptions['collection'],
    () => wrapWithPanel(filterOptions['collection'], refinementList, {
      container: `#${filterOptions['collection'].id}`,
      attribute: filterOptions['collection'].attribute,
      limit: 50,
    })),
  gender: new Filter(
    filterOptions['gender'],
    () => wrapWithPanel(filterOptions['gender'], refinementList, {
      container: `#${filterOptions['gender'].id}`,
      attribute: filterOptions['gender'].attribute,
      limit: 50,
    })),
  brand: new Filter(
    filterOptions['brand'],
    () => wrapWithPanel(filterOptions['brand'], refinementList, {
      container: `#${filterOptions['brand'].id}`,
      attribute: filterOptions['brand'].attribute,
      limit: 50,
      showMore: true,
      showMoreLimit: 1000,
    })),
  labGrown: new Filter(filterOptions['labGrown'], () => toggleRefinement({
    container: `#${filterOptions['labGrown'].id}`,
    attribute: filterOptions['labGrown'].attribute,
    on: 'Yes',
    templates: {
      labelText(_item, { html }) {
        return html`Lab Grown`;
      },
    },
  })),
  gemstone: new Filter(
    filterOptions['gemstone'],
    () => wrapWithPanel(filterOptions['gemstone'], refinementList, {
      container: `#${filterOptions['gemstone'].id}`,
      attribute: filterOptions['gemstone'].attribute,
      limit: 50,
    })),
  gemstoneShape: new Filter(
    filterOptions['gemstoneShape'],
    () => wrapWithPanel(filterOptions['gemstoneShape'], refinementList, {
      container: `#${filterOptions['gemstoneShape'].id}`,
      attribute: filterOptions['gemstoneShape'].attribute,
      limit: 50,
    })),
  diamondSettings: new Filter(
    filterOptions['diamondSettings'],
    () => wrapWithPanel(filterOptions['diamondSettings'], refinementList, {
      container: `#${filterOptions['diamondSettings'].id}`,
      attribute: filterOptions['diamondSettings'].attribute,
      limit: 50,
    })),
  diamondWeight: new Filter(
    filterOptions['diamondWeight'],
    () => wrapWithPanel(filterOptions['diamondWeight'], rangeSlider, {
      container: `#${filterOptions['diamondWeight'].id}`,
      attribute: filterOptions['diamondWeight'].attribute,
      pips: false,
      precision: 2,
      // step: 0.5, // It doesn't look like this supports < 1
      tooltips: {
        format: (rawValue) => `${rawValue} ct`,
      },
      cssClasses: {
        root: 'range-slider',
      },
    })),
  band: new Filter(
    filterOptions['band'],
    () => wrapWithPanel(filterOptions['band'], refinementList, {
      container: `#${filterOptions['band'].id}`,
      attribute: filterOptions['band'].attribute,
      limit: 50,
    })),
  caseMaterial: new Filter(
    filterOptions['caseMaterial'],
    () => wrapWithPanel(filterOptions['caseMaterial'], refinementList, {
      container: `#${filterOptions['caseMaterial'].id}`,
      attribute: filterOptions['caseMaterial'].attribute,
      limit: 50,
    })),
  caseSize: new Filter(
    filterOptions['caseSize'],
    () => wrapWithPanel(filterOptions['caseSize'], refinementList, {
      container: `#${filterOptions['caseSize'].id}`,
      attribute: filterOptions['caseSize'].attribute,
      limit: 50,
    })),
  centreStoneShape: new Filter(
    filterOptions['centreStoneShape'],
    () => wrapWithPanel(filterOptions['centreStoneShape'], refinementList, {
      container: `#${filterOptions['centreStoneShape'].id}`,
      attribute: filterOptions['centreStoneShape'].attribute,
      limit: 50,
    })),
  customization: new Filter(
    filterOptions['customization'],
    () => wrapWithPanel(filterOptions['customization'], refinementList, {
      container: `#${filterOptions['customization'].id}`,
      attribute: filterOptions['customization'].attribute,
      limit: 50,
    })),
  dialColour: new Filter(
    filterOptions['dialColour'],
    () => wrapWithPanel(filterOptions['dialColour'], refinementList, {
      container: `#${filterOptions['dialColour'].id}`,
      attribute: filterOptions['dialColour'].attribute,
      limit: 50,
    })),
  diameter: new Filter(
    filterOptions['diameter'],
    () => wrapWithPanel(filterOptions['diameter'], refinementList, {
      container: `#${filterOptions['diameter'].id}`,
      attribute: filterOptions['diameter'].attribute,
      limit: 50,
    })),
  durability: new Filter(
    filterOptions['durability'],
    () => wrapWithPanel(filterOptions['durability'], refinementList, {
      container: `#${filterOptions['durability'].id}`,
      attribute: filterOptions['durability'].attribute,
      limit: 50,
    })),
  features: new Filter(
    filterOptions['features'],
    () => wrapWithPanel(filterOptions['features'], refinementList, {
      container: `#${filterOptions['features'].id}`,
      attribute: filterOptions['features'].attribute,
      limit: 50,
    })),
  length: new Filter(
    filterOptions['length'],
    () => wrapWithPanel(filterOptions['length'], refinementList, {
      container: `#${filterOptions['length'].id}`,
      attribute: filterOptions['length'].attribute,
      limit: 50,
    })),
  material: new Filter(
    filterOptions['material'],
    () => wrapWithPanel(filterOptions['material'], refinementList, {
      container: `#${filterOptions['material'].id}`,
      attribute: filterOptions['material'].attribute,
      limit: 50,
    })),
  metalColour: new Filter(
    filterOptions['metalColour'],
    () => wrapWithPanel(filterOptions['metalColour'], refinementList, {
      container: `#${filterOptions['metalColour'].id}`,
      attribute: filterOptions['metalColour'].attribute,
      limit: 50,
    })),
  metalType: new Filter(
    filterOptions['metalType'],
    () => wrapWithPanel(filterOptions['metalType'], refinementList, {
      container: `#${filterOptions['metalType'].id}`,
      attribute: filterOptions['metalType'].attribute,
      limit: 50,
    })),
  movement: new Filter(
    filterOptions['movement'],
    () => wrapWithPanel(filterOptions['movement'], refinementList, {
      container: `#${filterOptions['movement'].id}`,
      attribute: filterOptions['movement'].attribute,
      limit: 50,
    })),
  primaryStone: new Filter(
    filterOptions['primaryStone'],
    () => wrapWithPanel(filterOptions['primaryStone'], refinementList, {
      container: `#${filterOptions['primaryStone'].id}`,
      attribute: filterOptions['primaryStone'].attribute,
      limit: 50,
    })),
  style: new Filter(
    filterOptions['style'],
    () => wrapWithPanel(filterOptions['style'], refinementList, {
      container: `#${filterOptions['style'].id}`,
      attribute: filterOptions['style'].attribute,
      limit: 50,
    })),
  waterResistance: new Filter(
    filterOptions['waterResistance'],
    () => wrapWithPanel(filterOptions['waterResistance'], refinementList, {
      container: `#${filterOptions['waterResistance'].id}`,
      attribute: filterOptions['waterResistance'].attribute,
      limit: 50,
      sortBy(a, b) {
        // For these fields, we want to sort by number, not alphabetically
        const aNum = parseFirstNumber(a.name);
        const bNum = parseFirstNumber(b.name);
        if (aNum === null) {
          return 1;
        }
        if (bNum === null) {
          return -1;
        }
        return aNum - bNum;
      },
    })),
  powerReserve: new Filter(
    filterOptions['powerReserve'],
    () => wrapWithPanel(filterOptions['powerReserve'], refinementList, {
      container: `#${filterOptions['powerReserve'].id}`,
      attribute: filterOptions['powerReserve'].attribute,
      limit: 50,
      sortBy(a, b) {
        // For these fields, we want to sort by number, not alphabetically
        const aNum = parseFirstNumber(a.name);
        const bNum = parseFirstNumber(b.name);
        if (aNum === null) {
          return 1;
        }
        if (bNum === null) {
          return -1;
        }
        return aNum - bNum;
      },
    })),
  earringStyle: new Filter(
    filterOptions['earringStyle'],
    () => wrapWithPanel(filterOptions['earringStyle'], refinementList, {
      container: `#${filterOptions['earringStyle'].id}`,
      attribute: filterOptions['earringStyle'].attribute,
      limit: 50,
    })),
  braceletStyle: new Filter(
    filterOptions['braceletStyle'],
    () => wrapWithPanel(filterOptions['braceletStyle'], refinementList, {
      container: `#${filterOptions['braceletStyle'].id}`,
      attribute: filterOptions['braceletStyle'].attribute,
      limit: 50,
    })),
  bandType: new Filter(
    filterOptions['bandType'],
    () => wrapWithPanel(filterOptions['bandType'], refinementList, {
      container: `#${filterOptions['bandType'].id}`,
      attribute: filterOptions['bandType'].attribute,
      limit: 50,
    })),
  giaCertified: new Filter(filterOptions['giaCertified'], () => toggleRefinement({
    container: `#${filterOptions['giaCertified'].id}`,
    attribute: filterOptions['giaCertified'].attribute,
    on: 'Yes',
    templates: {
      labelText(_item, { html }) {
        return html`GIA Certified`;
      },
    },
  })),
  limitedEdition: new Filter(filterOptions['limitedEdition'], () => toggleRefinement({
    container: `#${filterOptions['limitedEdition'].id}`,
    attribute: filterOptions['limitedEdition'].attribute,
    on: 'Yes',
    templates: {
      labelText(_item, { html }) {
        return html`Limited Edition`;
      },
    },
  })),
  newArrival: new Filter(filterOptions['newArrival'], () => toggleRefinement({
    container: `#${filterOptions['newArrival'].id}`,
    attribute: filterOptions['newArrival'].attribute,
    on: 'Yes',
    templates: {
      labelText(_item, { html }) {
        return html`New Arrivals`;
      },
    },
  })),
};

/** Return all filters except for specific ones */
function allFilterObjectsExcept(filterIds: string[]) {
  return Object.values(filterObjects).filter((filter) => !filterIds.includes(filter.id));
}

/** Map of the filter objects to use for each collection, by handle */
const collectionFilterObjects: Record<string, Filter[]> = {
  // Just show all by default
  default: [...Object.values(filterObjects)],
  'timepieces-tudor': [...Object.values(filterObjects)],

  // Special collections (and their subcategories)
  'timepieces': allFilterObjectsExcept(['labGrown', 'diamondWeight', 'giaCertified']),
  'jewellery': allFilterObjectsExcept(['limitedEdition']),
  'wedding-engagement': allFilterObjectsExcept(['limitedEdition', 'availableOnline']),
  'gifts-accessories': allFilterObjectsExcept(['labGrown', 'diamondWeight', 'giaCertified']),
};

/** Get the filters to use for a given collection handle */
export default function getFilterObjects(collectionHandle: string, slug?: string): Filter[] {
  const filterIdentifier = getFilterSetIdentifier(collectionHandle, slug);
  // console.debug('Filter objects identifier:', filterIdentifier);
  const filters = collectionFilterObjects[filterIdentifier];
  if (!filters) {
    return [];
  }
  return filters;
}
